// src/pages/student/PublicationStatus.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Chip,
    Grid,
    Slide,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Tabs,
    Tab,
    Avatar,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    InputAdornment,
    LinearProgress,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Article,
    Event,
    MenuBook,
    Assignment,
    Description,
    Schedule,
    Send,
    Visibility,
    Feedback,
    CheckCircle,
    Verified,
    Cancel,
    PictureAsPdf,
    Link as LinkIcon,
    Science,
    TrendingUp,
    Save,
    CloudUpload,
    Delete,
    Download
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIPublicationList,
    APIPublicationStats,
    APIPublicationStore,
    APIPublicationUpdate,
    APIPublicationDelete
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named StudentBar.js
// but it now renders "Researcher Portal" as its visible title.
import StudentBar from '../../components/StudentBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PublicationStatus = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [publications, setPublications] = useState([]);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPub, setEditingPub] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPub, setSelectedPub] = useState(null);
    const [formData, setFormData] = useState({
        publication_title: '',
        publication_type: 'journal_article',
        publication_status: 'planned',
        journal_name: '',
        abstract: '',
        co_authors: [],
        submission_date: '',
        publication_date: '',
        doi: '',
        volume: '',
        issue: '',
        pages: '',
        impact_factor: '',
        journal_quartile: '',
        publication_url: '',
        publisher: '',
        notes: ''
    });
    const [coAuthorInput, setCoAuthorInput] = useState('');
    const [pdfFile, setPdfFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isMounted = useRef(true);
    const studentId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // Publication type options
    const publicationTypes = [
        { value: 'journal_article', label: 'Journal Article', icon: <Article /> },
        { value: 'conference_paper', label: 'Conference Paper', icon: <Event /> },
        { value: 'book_chapter', label: 'Book Chapter', icon: <MenuBook /> },
        { value: 'patent', label: 'Patent', icon: <Assignment /> },
        { value: 'preprint', label: 'Preprint', icon: <Description /> }
    ];

    const publicationStatuses = [
        { value: 'planned', label: 'Planned', color: 'default' },
        { value: 'in_preparation', label: 'In Preparation', color: 'info' },
        { value: 'submitted', label: 'Submitted', color: 'warning' },
        { value: 'under_review', label: 'Under Review', color: 'info' },
        { value: 'revision_requested', label: 'Revision Requested', color: 'warning' },
        { value: 'accepted', label: 'Accepted', color: 'success' },
        { value: 'published', label: 'Published', color: 'success' },
        { value: 'rejected', label: 'Rejected', color: 'error' }
    ];

    const journalQuartiles = [
        { value: 'Q1', label: 'Q1 (Top 25%)', color: 'success' },
        { value: 'Q2', label: 'Q2 (25-50%)', color: 'info' },
        { value: 'Q3', label: 'Q3 (50-75%)', color: 'warning' },
        { value: 'Q4', label: 'Q4 (Bottom 25%)', color: 'error' },
        { value: 'unranked', label: 'Unranked', color: 'default' }
    ];

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);

        try {
            await Promise.all([
                fetchPublications(),
                fetchStats()
            ]);
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchPublications = async () => {
        const result = await apiService.listById(APIPublicationList, studentId);
        if (result.success && isMounted.current) {
            setPublications(Array.isArray(result.data) ? result.data : []);
        }
    };

    const fetchStats = async () => {
        const result = await apiService.listById(APIPublicationStats, studentId);
        if (result.success && result.data && isMounted.current) {
            setStats(result.data);
        }
    };

    const handleAddCoAuthor = () => {
        if (coAuthorInput.trim() && !formData.co_authors.includes(coAuthorInput.trim())) {
            setFormData(prev => ({
                ...prev,
                co_authors: [...prev.co_authors, coAuthorInput.trim()]
            }));
            setCoAuthorInput('');
        }
    };

    const handleRemoveCoAuthor = (author) => {
        setFormData(prev => ({
            ...prev,
            co_authors: prev.co_authors.filter(a => a !== author)
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setPdfFile(file);
        } else {
            showToast("Error", "Please upload a PDF file", "error");
        }
    };

    const handleOpenAdd = () => {
        setEditingPub(null);
        setFormData({
            publication_title: '',
            publication_type: 'journal_article',
            publication_status: 'planned',
            journal_name: '',
            abstract: '',
            co_authors: [],
            submission_date: '',
            publication_date: '',
            doi: '',
            volume: '',
            issue: '',
            pages: '',
            impact_factor: '',
            journal_quartile: '',
            publication_url: '',
            publisher: '',
            notes: ''
        });
        setCoAuthorInput('');
        setPdfFile(null);
        setOpenDialog(true);
    };

    const handleOpenEdit = (pub) => {
        setEditingPub(pub);
        setFormData({
            publication_title: pub.publication_title || '',
            publication_type: pub.publication_type || 'journal_article',
            publication_status: pub.publication_status || 'planned',
            journal_name: pub.journal_name || '',
            abstract: pub.abstract || '',
            co_authors: pub.co_authors || [],
            submission_date: pub.submission_date || '',
            publication_date: pub.publication_date || '',
            doi: pub.doi || '',
            volume: pub.volume || '',
            issue: pub.issue || '',
            pages: pub.pages || '',
            impact_factor: pub.impact_factor || '',
            journal_quartile: pub.journal_quartile || '',
            publication_url: pub.publication_url || '',
            publisher: pub.publisher || '',
            notes: pub.notes || ''
        });
        setCoAuthorInput('');
        setPdfFile(null);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingPub(null);
        setPdfFile(null);
    };

    const handleSave = async () => {
        setIsSubmitting(true);
        
        const formDataToSend = new FormData();
        formDataToSend.append('student_id', studentId);
        formDataToSend.append('publication_title', formData.publication_title);
        formDataToSend.append('publication_type', formData.publication_type);
        formDataToSend.append('publication_status', formData.publication_status);
        formDataToSend.append('journal_name', formData.journal_name);
        formDataToSend.append('abstract', formData.abstract);
        formDataToSend.append('co_authors', JSON.stringify(formData.co_authors));
        formDataToSend.append('submission_date', formData.submission_date);
        formDataToSend.append('publication_date', formData.publication_date);
        formDataToSend.append('doi', formData.doi);
        formDataToSend.append('volume', formData.volume);
        formDataToSend.append('issue', formData.issue);
        formDataToSend.append('pages', formData.pages);
        formDataToSend.append('impact_factor', formData.impact_factor);
        formDataToSend.append('journal_quartile', formData.journal_quartile);
        formDataToSend.append('publication_url', formData.publication_url);
        formDataToSend.append('publisher', formData.publisher);
        formDataToSend.append('notes', formData.notes);
        
        if (pdfFile) {
            formDataToSend.append('pdf_file', pdfFile);
        }
        
        if (editingPub) {
            formDataToSend.append('updateId', editingPub.id);
        }

        try {
            let result;
            if (editingPub) {
                result = await apiService.update(APIPublicationUpdate, formDataToSend);
            } else {
                result = await apiService.create(APIPublicationStore, formDataToSend);
            }
            
            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseDialog();
                fetchAllData();
            }
        } catch (error) {
            showToast("Error", "Failed to save publication", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (pub) => {
        const formData = new FormData();
        formData.append('updateId', pub.id);
        
        try {
            const result = await apiService.delete(APIPublicationDelete, formData);
            if (result && result.success) {
                showToast("Success", "Publication deleted successfully", "success");
                fetchAllData();
            }
        } catch (error) {
            showToast("Error", "Failed to delete publication", "error");
        }
        handleMenuClose();
    };

    const handleDownloadPDF = (pub) => {
        if (pub.pdf_url) {
            window.open(pub.pdf_url, '_blank');
        }
        handleMenuClose();
    };

    const handleMenuOpen = (event, pub) => {
        setSelectedPub(pub);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedPub(null);
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'planned': return <Schedule />;
            case 'in_preparation': return <EditIcon />;
            case 'submitted': return <Send />;
            case 'under_review': return <Visibility />;
            case 'revision_requested': return <Feedback />;
            case 'accepted': return <CheckCircle />;
            case 'published': return <Verified />;
            case 'rejected': return <Cancel />;
            default: return <Schedule />;
        }
    };

    const getTypeIcon = (type) => {
        const found = publicationTypes.find(t => t.value === type);
        return found?.icon || <Article />;
    };

    if (loading) {
        return <LoadingState message="Loading Publications..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Publications" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
            <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                <StudentBar notificationCount={5}/>

                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Science /> Publication Status
                            </Typography>

                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'center', sm: 'flex-start' }
                            }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenAdd}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                >
                                    {isMobile ? 'Add' : 'Add Publication'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="info"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchAllData}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Box>

                        {/* Stats Cards */}
                        {stats && (
                            <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.info.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.total || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Total Publications
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.success.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.published || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Published/Accepted
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.warning.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.under_review || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Under Review
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.secondary.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.journal_articles || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Journal Articles
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Box>
                {/* Publications Grid */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    {publications.length === 0 ? (
                        <Paper sx={{ 
                            p: { xs: 4, sm: 8 }, 
                            textAlign: 'center', 
                            borderRadius: 3,
                            backgroundColor: 'white'
                        }}>
                            <Science sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                No publications added yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Click "Add Publication" to track your research publications
                            </Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={2}>
                            {publications.map((pub, index) => {
                                const statusColor = pub.publication_status_color;
                                const typeIcon = getTypeIcon(pub.publication_type);
                                const statusIcon = getStatusIcon(pub.publication_status);
                                
                                return (
                                    <Grid item xs={12} sm={6} lg={4} key={pub.id}>
                                        <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                            <Card sx={{ 
                                                height: '100%', 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: theme.shadows[8]
                                                }
                                            }}>
                                                <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        justifyContent: 'space-between', 
                                                        alignItems: 'flex-start', 
                                                        mb: 2,
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        gap: { xs: 1, sm: 0 }
                                                    }}>
                                                        <Chip
                                                            icon={typeIcon}
                                                            label={pub.formatted_publication_type}
                                                            size="small"
                                                            color={pub.publication_type_color}
                                                            variant="outlined"
                                                        />
                                                        <Chip
                                                            icon={statusIcon}
                                                            label={pub.formatted_publication_status}
                                                            size="small"
                                                            color={statusColor}
                                                        />
                                                    </Box>
                                                    
                                                    <Typography variant="h6" sx={{ 
                                                        fontWeight: 'bold', 
                                                        mb: 1, 
                                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                                    }}>
                                                        {pub.publication_title}
                                                    </Typography>
                                                    
                                                    {pub.journal_name && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {pub.journal_name}
                                                            {pub.volume && `, Vol. ${pub.volume}`}
                                                            {pub.issue && `(${pub.issue})`}
                                                            {pub.pages && `, pp. ${pub.pages}`}
                                                        </Typography>
                                                    )}
                                                    
                                                    {pub.doi && (
                                                        <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 1, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            DOI: {pub.doi}
                                                        </Typography>
                                                    )}
                                                    
                                                    {pub.impact_factor && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                            <TrendingUp fontSize="small" color="action" />
                                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                                IF: {pub.formatted_impact_factor}
                                                                {pub.journal_quartile && ` • ${pub.formatted_journal_quartile}`}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    
                                                    {pub.abstract && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ 
                                                            mt: 2,
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                        }}>
                                                            {pub.abstract.length > (isMobile ? 100 : 150) 
                                                                ? pub.abstract.substring(0, isMobile ? 100 : 150) + '...' 
                                                                : pub.abstract}
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                                
                                                <CardActions sx={{ 
                                                    p: 2, 
                                                    pt: 0, 
                                                    justifyContent: 'space-between',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    gap: { xs: 1, sm: 0 }
                                                }}>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        {pub.publication_url && (
                                                            <Tooltip title="View Online">
                                                                <IconButton size="small" href={pub.publication_url} target="_blank">
                                                                    <LinkIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {pub.pdf_url && (
                                                            <Tooltip title="Download PDF">
                                                                <IconButton size="small" onClick={() => handleDownloadPDF(pub)}>
                                                                    <PictureAsPdf fontSize="small" color="error" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, pub)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        </Zoom>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </motion.div>

                {/* Publication Actions Menu */}
                <Menu 
                    anchorEl={anchorEl} 
                    open={Boolean(anchorEl)} 
                    onClose={handleMenuClose}
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            minWidth: 150
                        }
                    }}
                >
                    <MenuItem onClick={() => { handleOpenEdit(selectedPub); handleMenuClose(); }}>
                        <EditIcon sx={{ mr: 1 }} /> Edit
                    </MenuItem>
                    <MenuItem onClick={() => handleDelete(selectedPub)} sx={{ color: 'error.main' }}>
                        <DeleteIcon sx={{ mr: 1 }} /> Delete
                    </MenuItem>
                </Menu>

                {/* Add/Edit Publication Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseDialog();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center',
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        <b>{editingPub ? 'Edit Publication' : 'Add New Publication'}</b>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ p: { xs: 1, sm: 2 } }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Publication Title *"
                                        name="publication_title"
                                        value={formData.publication_title}
                                        onChange={handleInputChange}
                                        required
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                        <InputLabel>Publication Type *</InputLabel>
                                        <Select
                                            name="publication_type"
                                            value={formData.publication_type}
                                            onChange={handleInputChange}
                                            label="Publication Type *"
                                        >
                                            {publicationTypes.map(type => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {type.icon}
                                                        {type.label}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                        <InputLabel>Publication Status *</InputLabel>
                                        <Select
                                            name="publication_status"
                                            value={formData.publication_status}
                                            onChange={handleInputChange}
                                            label="Publication Status *"
                                        >
                                            {publicationStatuses.map(status => (
                                                <MenuItem key={status.value} value={status.value}>
                                                    {status.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Journal/Conference Name"
                                        name="journal_name"
                                        value={formData.journal_name}
                                        onChange={handleInputChange}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Volume"
                                        name="volume"
                                        value={formData.volume}
                                        onChange={handleInputChange}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Issue"
                                        name="issue"
                                        value={formData.issue}
                                        onChange={handleInputChange}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Pages"
                                        name="pages"
                                        value={formData.pages}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 1-15"
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Submission Date"
                                        type="date"
                                        name="submission_date"
                                        value={formData.submission_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Publication Date"
                                        type="date"
                                        name="publication_date"
                                        value={formData.publication_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                        Co-authors
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Add co-author name"
                                            value={coAuthorInput}
                                            onChange={(e) => setCoAuthorInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleAddCoAuthor()}
                                        />
                                        <Button variant="outlined" onClick={handleAddCoAuthor} size={isMobile ? "small" : "medium"}>
                                            Add
                                        </Button>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {formData.co_authors.map((author, idx) => (
                                            <Chip
                                                key={idx}
                                                label={author}
                                                onDelete={() => handleRemoveCoAuthor(author)}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Abstract"
                                        name="abstract"
                                        value={formData.abstract}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={4}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Notes"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={2}
                                        placeholder="Additional notes or comments..."
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        component="label"
                                        startIcon={<CloudUpload />}
                                        sx={{ mb: 1 }}
                                        size={isMobile ? "small" : "medium"}
                                    >
                                        Upload PDF File
                                        <input
                                            type="file"
                                            hidden
                                            accept=".pdf"
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                    {pdfFile && (
                                        <Chip
                                            label={pdfFile.name}
                                            onDelete={() => setPdfFile(null)}
                                            color="primary"
                                            size="small"
                                        />
                                    )}
                                    {editingPub?.pdf_url && !pdfFile && (
                                        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                                            Current PDF: <a href={editingPub.pdf_url} target="_blank">Download</a>
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button 
                            color="error" 
                            onClick={handleCloseDialog} 
                            variant="outlined" 
                            disabled={isSubmitting}
                            size={isMobile ? "small" : "medium"}
                        >
                            Cancel
                        </Button>
                        <Button 
                            color="primary" 
                            onClick={handleSave} 
                            variant="contained"
                            disabled={isSubmitting || !formData.publication_title}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
                            size={isMobile ? "small" : "medium"}
                        >
                            {isSubmitting ? 'Saving...' : (editingPub ? 'Update' : 'Save')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default PublicationStatus;